// Confidence bands — replaces the hard 0-100 cutoffs that used to make a
// one-point difference in a self-report score categorically meaningful.
//
// Intent decision 3 (docs/intent/001-diagnostic-redesign/intent.md): every hard
// cutoff becomes a band with an explicit uncertain zone between the confident
// zones. A score landing in an uncertain zone gets a compound label
// ("Low–Medium") and must be treated as neutral by anything that adapts.
//
// ─────────────────────────────────────────────────────────────────────────────
// EVERY EDGE BELOW IS PROVISIONAL. They are engineering judgement, not evidence.
// Beta wave 1 (50 users, CogniPrint_Beta_Programme_Design_v1) sets them from the
// observed score distribution. Until then, nothing in the product may branch on
// a difference smaller than the width of an uncertain zone.
// ─────────────────────────────────────────────────────────────────────────────

/** One band on a 0-100 scale. `uncertain` bands sit between two confident ones. */
export interface ScoreBand {
  /** Inclusive lower bound. */
  min: number;
  /** Inclusive upper bound. */
  max: number;
  /** The label shown to the learner. */
  label: string;
  /** True for the transition zones between two confident bands. */
  uncertain: boolean;
}

/** Information Density and Viewpoint Range share a Low / Medium / High scale. */
export const LOW_MEDIUM_HIGH_BANDS: ScoreBand[] = [
  { min: 0,  max: 27,  label: 'Low',         uncertain: false },
  { min: 28, max: 38,  label: 'Low–Medium',  uncertain: true  },
  { min: 39, max: 60,  label: 'Medium',      uncertain: false },
  { min: 61, max: 71,  label: 'Medium–High', uncertain: true  },
  { min: 72, max: 100, label: 'High',        uncertain: false },
];

/** Depth Tolerance: four confident bands, three transition zones. */
export const DEPTH_TOLERANCE_BANDS: ScoreBand[] = [
  { min: 0,  max: 20,  label: 'Essentials',            uncertain: false },
  { min: 21, max: 29,  label: 'Essentials–Standard',   uncertain: true  },
  { min: 30, max: 45,  label: 'Standard',              uncertain: false },
  { min: 46, max: 54,  label: 'Standard–Deep',         uncertain: true  },
  { min: 55, max: 70,  label: 'Deep',                  uncertain: false },
  { min: 71, max: 79,  label: 'Deep–Exhaustive',       uncertain: true  },
  { min: 80, max: 100, label: 'Exhaustive',            uncertain: false },
];

/**
 * Learning Approach axes. Each axis is a 0-100 continuum; the old code split it
 * at `> 50`, so 50 and 51 landed in different Kolb quadrants. 41–59 is now
 * explicitly balanced — the quadrant label is not stable in there.
 */
export const LEARNING_AXIS_BALANCED_MIN = 41;
export const LEARNING_AXIS_BALANCED_MAX = 59;

export function isLearningAxisBalanced(score: number): boolean {
  return score >= LEARNING_AXIS_BALANCED_MIN && score <= LEARNING_AXIS_BALANCED_MAX;
}

function clampScore(score: number): number {
  if (!Number.isFinite(score)) return 50;
  return Math.max(0, Math.min(100, score));
}

/** The band a score falls in. Never returns undefined — the tables cover 0-100. */
export function bandFor(bands: ScoreBand[], score: number): ScoreBand {
  const s = clampScore(score);
  return bands.find((b) => s >= b.min && s <= b.max) ?? bands[bands.length - 1];
}

/** The learner-facing label for a score. */
export function bandLabel(bands: ScoreBand[], score: number): string {
  return bandFor(bands, score).label;
}

/**
 * True when a score sits in a transition zone. Anything that ADAPTS on a
 * dimension must check this and fall back to its neutral default — an uncertain
 * score is not evidence of a preference, it is evidence of not knowing.
 */
export function isUncertain(bands: ScoreBand[], score: number): boolean {
  return bandFor(bands, score).uncertain;
}
